import { TDynamicForm, TDynamicFormField } from "../../form-elements/dynamic-form/dynamic-form";
import { TDynamicFormConfig } from "./company-forms";

const sharedFields: Array<TDynamicFormField> = [
  {
    type: "text",
    name: "subject",
    label: "Ämne",
    rules: { required: true },
  },
  {
    type: "textarea",
    name: "description",
    label: "Beskriv ditt ärende",
    rules: { required: true },
    maxLength: 1000,
  },
  {
    type: "text",
    name: "firstName",
    label: "Förnamn",
    autoComplete: "given-name",
    rules: { required: true },
  },
  {
    type: "text",
    name: "lastName",
    label: "Efternamn",
    autoComplete: "family-name",
    rules: { required: true },
  },
  {
    type: "text",
    name: "email",
    label: "E-post*",
    autoComplete: "email",
    rules: { required: true },
  },
  {
    type: "text",
    name: "phone",
    label: "Telefonnummer eller mobil*",
    autoComplete: "tel",
    rules: { required: true },
  },
  {
    type: "fileupload",
    name: "attachedFiles",
    label: "Bifoga filer",
    helperText: "Bifoga filer som hjälper oss att hjälpa dig.",
    allowMultipleFiles: true,
  },
  {
    type: "checkbox",
    name: "termsAccepted",
    label: "Villkor",
    rules: { required: true },
    checkboxes: [
      {
        name: "termsAccepted",
        value: "true",
        label: "Jag samtycker härmed till att Preem AB behandlar mina personuppgifter enligt ovan",
      },
    ],
  },
];

const B2CApps = {
  mode: "onSubmit",
  reValidateMode: "onChange",
  fields: [
    {
      type: "content",
      name: "heading",
      value: <h5>Appar</h5>,
    },
    ...sharedFields,
  ],
} satisfies TDynamicForm;

const B2CProductQuestion = {
  mode: "onSubmit",
  reValidateMode: "onChange",
  fields: [
    {
      type: "content",
      name: "heading",
      value: <h5>Produktfråga</h5>,
    },
    ...sharedFields,
  ],
} satisfies TDynamicForm;

const B2CStationQuestion = {
  mode: "onSubmit",
  reValidateMode: "onChange",
  fields: [
    {
      type: "content",
      name: "heading",
      value: <h5>Stationsfråga</h5>,
    },
    {
      type: "select",
      name: "subCategory",
      label: "Underkategori",
      rules: { required: true },
      items: [
        {
          label: "Felanmälan",
          value: "Report Fault",
        },
        {
          label: "Fråga",
          value: "inquiry",
        },
        {
          label: "Önskemål",
          value: "other",
        },
        {
          label: "Övrigt",
          value: "other",
        },
      ],
    },
    ...sharedFields,
  ],
} satisfies TDynamicForm;

const B2CPreemMedlem = {
  mode: "onSubmit",
  reValidateMode: "onChange",
  fields: [
    {
      type: "content",
      name: "heading",
      value: <h5>Preem Medlem</h5>,
    },
    {
      type: "select",
      name: "subCategory",
      label: "Underkategori",
      rules: { required: true },
      items: [
        {
          label: "Rabatt",
          value: "ABK-Rebate",
        },
        {
          label: "Koppla kort",
          value: "ABK-Connect",
        },
        {
          label: "Övrigt",
          value: "ABK-Other",
        },
      ],
    },
    ...sharedFields,
  ],
} satisfies TDynamicForm;

const B2CPreemPrivatkortMastercard = {
  mode: "onSubmit",
  reValidateMode: "onChange",
  fields: [
    {
      type: "content",
      name: "heading",
      value: (
        <>
          <h5>Preem Privatkort och Preem Mastercard</h5>
          <p>
            Preem Privatkort och Preem Mastercard hanteras av Ikano och vi hänvisar därför era frågor till dem.
            Kontaktuppgifter hittar ni <a href="https://www.preem.se/om-preem/kontakta-oss/privatkund/">här.</a>
          </p>
        </>
      ),
    },
  ],
} satisfies TDynamicForm;

const B2CKvitto = {
  mode: "onSubmit",
  reValidateMode: "onChange",
  fields: [
    {
      type: "content",
      name: "heading",
      value: (
        <>
          <h5>Kvitto</h5>
          <p>För att få fram ett kvitto behöver vi följande uppgifter:</p>
          <ul>
            <li>
              Station som tankningen skedde på. Se våra stationer <a href="/pa-stationen/hitta-station">här.</a>
            </li>
            <li>Datum för tankning (ej datum på transaktionen i banken) </li>
            <li>Sex första och fyra sista siffrorna på kortet. Ex. 123456******1234</li>
            <li>Belopp på tankningen</li>
          </ul>
          <p>
            Om alla uppgifter inte meddelas eller om någon är inkorrekt förbehåller vi oss rätten att neka er
            kvittoförfrågan.
          </p>
          <p>
            Obs! Om ni har betalat med en digital plånbok, exempelvis telefon eller klocka är det inte samma kortnummer
            som på det fysiska kortet. Kolla om ni kan få fram kortnumret från er bank eller ur den digitala plånboken
            för att vi ska kunna få rätt uppgifter.
          </p>
        </>
      ),
    },
    {
      type: "text",
      name: "station",
      label: "Station",
    },
    {
      type: "text",
      name: "date",
      label: "Datum för tankning",
    },
    {
      type: "text",
      name: "cardNumber",
      label: "Sex första och fyra sista siffrorna på kortet.",
      helperText: "Ex. 123456******1234",
    },
    {
      type: "text",
      name: "amount",
      label: "Belopp",
    },
    ...sharedFields,
  ],
} satisfies TDynamicForm;

const B2COther = {
  mode: "onSubmit",
  reValidateMode: "onChange",
  fields: [
    {
      type: "content",
      name: "heading",
      value: <h5>Övrigt</h5>,
    },
    ...sharedFields,
  ],
} satisfies TDynamicForm;

const ConsumerForms = {
  apps: { label: "Appar", form: B2CApps },
  receipt: { label: "Kvitton", form: B2CKvitto },
  member: { label: "Preem Medlem", form: B2CPreemMedlem },
  card: { label: "Preem Privatkort / Mastercard", form: B2CPreemPrivatkortMastercard },
  product: { label: "Produktfråga", form: B2CProductQuestion },
  station: { label: "Stationsfråga", form: B2CStationQuestion },
  other: { label: "Övrigt", form: B2COther },
} satisfies Record<string, TDynamicFormConfig>;

export default ConsumerForms;
